import { useEffect, useRef } from 'react';
import type { SetURLSearchParams } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

type UseDashboardGuidedTourParams = {
  enabled: boolean;
  setSearchParams: SetURLSearchParams;
};

function getAddProjectTourElement(): Element {
  return (
    document.querySelector('[data-guided-tour="guided-tour-add-project"] button') ||
    document.querySelector('[data-guided-tour="guided-tour-add-project"]') ||
    document.body
  );
}

export function useDashboardGuidedTour({ enabled, setSearchParams }: UseDashboardGuidedTourParams): void {
  const guidedTourRef = useRef<ReturnType<typeof driver> | null>(null);
  const hasStartedGuidedTourRef = useRef(false);

  useEffect(() => {
    if (!enabled || hasStartedGuidedTourRef.current) {
      return;
    }

    const clearGuidedTourParam = () => {
      setSearchParams(
        (currentParams) => {
          const nextParams = new URLSearchParams(currentParams);
          nextParams.delete('guidedTour');
          return nextParams;
        },
        { replace: true }
      );
    };

    let attempts = 0;
    let timeoutId: number | null = null;
    let startDelayTimeoutId: number | null = null;

    const tryStartTour = () => {
      const addProjectButton = document.querySelector('[data-guided-tour="guided-tour-add-project"]');
      const accountNav = document.querySelector('[data-guided-tour="guided-tour-account-nav"]');
      const supportNav = document.querySelector('[data-guided-tour="guided-tour-support-nav"]');
      const guideNav = document.querySelector('[data-guided-tour="guided-tour-guide-nav"]');

      if (!addProjectButton || !accountNav || !supportNav || !guideNav) {
        attempts += 1;

        if (attempts >= 20) {
          clearGuidedTourParam();
          return;
        }

        timeoutId = window.setTimeout(tryStartTour, 200);
        return;
      }

      hasStartedGuidedTourRef.current = true;
      guidedTourRef.current = driver({
        showProgress: true,
        allowClose: true,
        overlayClickBehavior: () => {},
        overlayColor: '#1f2937',
        overlayOpacity: 0.32,
        stagePadding: 6,
        stageRadius: 14,
        popoverClass: 'um-guided-tour-popover',
        steps: [
          {
            element: getAddProjectTourElement,
            popover: {
              popoverClass: 'um-guided-tour-popover um-guided-tour-popover--project',
              title: 'Create your first project',
              description:
                'Use Add Project to create a new project and start working from there. Select your location and then it will be possible to add geodata and simulations.',
              side: 'left',
              align: 'center',
            },
          },
          {
            element: '[data-guided-tour="guided-tour-account-nav"]',
            popover: {
              title: 'Manage your account',
              description:
                'Open Account to manage users, organization settings, billing, and the rest of your workspace settings.',
              side: 'right',
              align: 'center',
            },
          },
          {
            element: '[data-guided-tour="guided-tour-support-nav"]',
            popover: {
              title: 'Get support',
              description: 'If you need any support, you can use this section to contact us.',
              side: 'right',
              align: 'center',
            },
          },
          {
            element: '[data-guided-tour="guided-tour-guide-nav"]',
            popover: {
              title: 'Open the guide',
              description: 'For anything else, you can always consult the user guide from this section.',
              side: 'right',
              align: 'center',
            },
          },
        ],
        onDestroyed: () => {
          clearGuidedTourParam();
        },
      });

      startDelayTimeoutId = window.setTimeout(() => {
        guidedTourRef.current?.drive();
      }, 1000);
    };

    tryStartTour();

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      if (startDelayTimeoutId) {
        window.clearTimeout(startDelayTimeoutId);
      }

      guidedTourRef.current?.destroy();
      guidedTourRef.current = null;
    };
  }, [enabled, setSearchParams]);
}
